import classNames from 'classnames'
import sortBy from 'lodash-es/sortBy'
import React, { FunctionComponent } from 'react'
import orderSummaryStyles from '../OrderSummary/OrderSummary.module.css'
import { EVENT_CATEGORIES } from '@/config/events.config'
import { isEventAddOn } from '@/utils/cart.utils'
// styles
import styles from './LineItems.module.css'
import LineItem from '@/app/_components/LineItem/LineItem'

export type SortableLocalLineItem = {
  displayOrder: number
} & Transformed.LineItem

export const sortByDisplayOrder = (
  items: SortableLocalLineItem[],
): SortableLocalLineItem[] => (
  sortBy(items, (item) => item.displayOrder)
)

type Props = {
  cart: Transformed.Cart
  // Todo: rename this its singular but should be pluralized since it renders lineItems within a ticket category
  /** this lineItems data to display */
  lineItemWithMeta: LocalLineItemWithMeta

  /** function that return the total quantity for a given ticket tier.
   *  i.e Child, Military, Senior are ticket tiers within General Admission (GA is a TicketCategory)
   * */
  adjustedQtyForTier: (args: QtyArgs) => number

  /** whether to show the delete button or not */
  showDeleteButton: boolean

  /** handle delete function */
  handleDelete?: ({ tickets, lineItems }: DeleteArgs) => void

  /** a list of Event Categories */
  type: Transformed.EventCategory[]
}

const LineItems: FunctionComponent<Props> = ({
  cart,
  lineItemWithMeta,
  adjustedQtyForTier,
  showDeleteButton,
  handleDelete = undefined,
  type,
}: Props) => {
  if (!lineItemWithMeta || !lineItemWithMeta.items || lineItemWithMeta.items?.length === 0) return null
  const { category, tiers, items, event } = lineItemWithMeta
  const nextTestId = `line-items-${type.map((el) => el.toLowerCase()).join('|')}`

  const sortedItems: SortableLocalLineItem[] = sortByDisplayOrder(
    items.map((item) => {
      const tier = tiers.find((t) => t.id === item.tier.id)
      const displayOrder = tier?.displayOrder || 0
      return {
        ...item,
        displayOrder,
      }
    }),
  )

  const isEventLineItem = type.length === EVENT_CATEGORIES.length
    && type.every((cat) => EVENT_CATEGORIES.indexOf(cat) > -1)

  const isEventAddOnLineItem = lineItemWithMeta.category.meta?.some(isEventAddOn)

  return (
    <div
      data-testid={isEventAddOnLineItem ? 'event-add-ons-line-items' : nextTestId}
      className={classNames(
        styles.LineItems,
        {
          [styles.Event]: isEventLineItem,
          [styles.AddOns]: isEventAddOnLineItem,
        },
      )}
    >
      <div className={orderSummaryStyles.HeaderWithRemoveAll}>
        <h2
          className={styles.TicketCategoryTitle}
          data-testid="ticket-category-title"
        >
          {category.title}
        </h2>
      </div>
      {sortedItems.map((item: SortableLocalLineItem) => {
        const {
          amount: price,
          ticketIds,
          tier: { id: tierId },
          timeslot,
        } = item
        const tier = tiers.find((t) => t.id === tierId)

        // exit if no tier
        if (!tier) return null

        const quantity = adjustedQtyForTier({
          categoryId: category.id,
          tierId,
          timeslotId: timeslot?.id,
        })

        const title = isEventLineItem && !isEventAddOnLineItem ? category.title : tier.title

        const eventSlug = isEventLineItem ? event.slug : undefined
        return (
          <LineItem
            key={`${tier.id}${timeslot?.id}`}
            eventSlug={eventSlug}
            title={title}
            quantity={quantity}
            price={price}
            ticketIds={[...(ticketIds as string[])]}
            cart={cart}
            showDeleteButton={showDeleteButton}
            handleDelete={handleDelete}
            lineItem={item}
            isEventLineItem={isEventLineItem}
          />
        )
      })}
    </div>
  )
}

export default LineItems
